import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

/**
 * Send LLM request
 */
const sendLLMRequest = async (prompt, options = {}) => {
  const response = await fetch(`${API_BASE_URL}/chat`, {
    method: 'POST',
    headers: DEFAULT_HEADERS,
    body: JSON.stringify({
      message: prompt,
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 500,
    }),
  });

  if (!response.ok) {
    throw new Error('LLM request failed');
  }

  const data = await response.json();
  return data.response || data.message || '';
};

/**
 * Entity Generator Service
 * AI-powered generation of Tasks, Projects, and Courses from context
 */

/**
 * Generate a task from a project
 * @param {object} project - Project object with context
 * @param {object} options - Additional options (e.g., focus area, priority)
 */
export const generateTaskFromProject = async (project, options = {}) => {
  try {
    const prompt = `You are a task breakdown expert. Analyze the following project and generate a specific, actionable task.

Project Context:
- Title: ${project.title || 'Untitled Project'}
- Description: ${project.description || 'No description'}
- Goals: ${project.goals ? JSON.stringify(project.goals) : 'No goals specified'}
- Status: ${project.status || 'Not specified'}

${options.focusArea ? `Focus Area: ${options.focusArea}` : ''}
${options.priority ? `Priority: ${options.priority}` : ''}

Generate a task that contributes to this project. Return ONLY a JSON object with this exact structure:
{
  "activity_who": "Role or person responsible (e.g., 'Developer', 'Designer')",
  "activity_what": "Clear, actionable task description (50-100 chars)",
  "activity_why": "Purpose and value of this task (100-200 chars)",
  "activity_how": "Approach or method to complete the task (100-200 chars)",
  "estimated_minutes": <number between 15 and 480>,
  "scheduled_time": "<ISO 8601 datetime, suggest within next 7 days>",
  "generated_from": "project",
  "generation_context": {
    "project_id": "${project.id}",
    "project_title": "${project.title}",
    "focus_area": "${options.focusArea || 'general'}"
  }
}

Return ONLY the JSON object, no markdown formatting, no explanation.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 500,
    });

    // Parse the response
    const taskData = JSON.parse(response);

    return { data: taskData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating task from project:', error);
    return { data: null, error };
  }
};

/**
 * Generate a task from a course
 * @param {object} course - Course object with context
 * @param {object} options - Additional options
 */
export const generateTaskFromCourse = async (course, options = {}) => {
  try {
    const prompt = `You are a learning task expert. Analyze the following course and generate a specific learning task.

Course Context:
- Title: ${course.title || 'Untitled Course'}
- Description: ${course.description || 'No description'}
- Topics: ${course.topics ? JSON.stringify(course.topics) : 'No topics specified'}
- Level: ${course.level || 'Not specified'}

${options.module ? `Module: ${options.module}` : ''}
${options.taskType ? `Task Type: ${options.taskType}` : ''}

Generate a learning task for this course. Return ONLY a JSON object with this exact structure:
{
  "activity_who": "Learner or student role",
  "activity_what": "Clear learning task description",
  "activity_why": "Learning objective and value",
  "activity_how": "Learning approach or method",
  "estimated_minutes": <number between 30 and 240>,
  "scheduled_time": "<ISO 8601 datetime>",
  "generated_from": "course",
  "generation_context": {
    "course_id": "${course.id}",
    "course_title": "${course.title}",
    "module": "${options.module || 'general'}"
  }
}

Return ONLY the JSON object, no markdown formatting.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 500,
    });

    const taskData = JSON.parse(response);

    return { data: taskData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating task from course:', error);
    return { data: null, error };
  }
};

/**
 * Generate a project from a course
 * @param {object} course - Course object with context
 * @param {object} options - Additional options
 */
export const generateProjectFromCourse = async (course, options = {}) => {
  try {
    const prompt = `You are a project planning expert. Analyze the following course and generate a comprehensive project.

Course Context:
- Title: ${course.title || 'Untitled Course'}
- Description: ${course.description || 'No description'}
- Topics: ${course.topics ? JSON.stringify(course.topics) : 'No topics specified'}
- Learning Objectives: ${course.learning_objectives ? JSON.stringify(course.learning_objectives) : 'Not specified'}

${options.projectType ? `Project Type: ${options.projectType}` : ''}

Generate a project that applies the course concepts. Return ONLY a JSON object with this exact structure:
{
  "title": "Clear project title (30-60 chars)",
  "description": "Detailed project description (200-400 chars)",
  "goals": ["Goal 1", "Goal 2", "Goal 3"],
  "milestones": [
    {"title": "Milestone 1", "description": "Description", "deadline": "ISO date"},
    {"title": "Milestone 2", "description": "Description", "deadline": "ISO date"}
  ],
  "status": "planning",
  "generated_from": "course",
  "generation_context": {
    "course_id": "${course.id}",
    "course_title": "${course.title}"
  }
}

Return ONLY the JSON object, no markdown formatting.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 800,
    });

    const projectData = JSON.parse(response);

    return { data: projectData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating project from course:', error);
    return { data: null, error };
  }
};

/**
 * Generate a project from a task
 * @param {object} task - Task object with context
 * @param {object} options - Additional options
 */
export const generateProjectFromTask = async (task, options = {}) => {
  try {
    const prompt = `You are a project expansion expert. Analyze the following task and expand it into a comprehensive project.

Task Context:
- What: ${task.activity_what || 'Untitled Task'}
- Why: ${task.activity_why || 'No purpose specified'}
- How: ${task.activity_how || 'No method specified'}
- Who: ${task.activity_who || 'Not specified'}

${options.scope ? `Desired Scope: ${options.scope}` : ''}

Expand this task into a full project. Return ONLY a JSON object with this exact structure:
{
  "title": "Project title based on task",
  "description": "Expanded project description",
  "goals": ["Goal 1", "Goal 2", "Goal 3"],
  "milestones": [
    {"title": "Milestone 1", "description": "Description", "deadline": "ISO date"}
  ],
  "status": "planning",
  "generated_from": "task",
  "generation_context": {
    "task_id": "${task.id}",
    "task_what": "${task.activity_what}"
  }
}

Return ONLY the JSON object, no markdown formatting.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 800,
    });

    const projectData = JSON.parse(response);

    return { data: projectData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating project from task:', error);
    return { data: null, error };
  }
};

/**
 * Generate a course from a project
 * @param {object} project - Project object with context
 * @param {object} options - Additional options
 */
export const generateCourseFromProject = async (project, options = {}) => {
  try {
    const prompt = `You are a curriculum design expert. Analyze the following project and create a structured course.

Project Context:
- Title: ${project.title || 'Untitled Project'}
- Description: ${project.description || 'No description'}
- Goals: ${project.goals ? JSON.stringify(project.goals) : 'No goals'}

${options.targetAudience ? `Target Audience: ${options.targetAudience}` : ''}

Create a course that teaches the skills needed for this project. Return ONLY a JSON object with this exact structure:
{
  "title": "Course title",
  "description": "Course overview (200-400 chars)",
  "learning_objectives": ["Objective 1", "Objective 2", "Objective 3"],
  "topics": ["Topic 1", "Topic 2", "Topic 3"],
  "modules": [
    {"title": "Module 1", "description": "Description", "duration_hours": 2},
    {"title": "Module 2", "description": "Description", "duration_hours": 3}
  ],
  "level": "intermediate",
  "prerequisites": ["Prerequisite 1", "Prerequisite 2"],
  "generated_from": "project",
  "generation_context": {
    "project_id": "${project.id}",
    "project_title": "${project.title}"
  }
}

Return ONLY the JSON object, no markdown formatting.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 1000,
    });

    const courseData = JSON.parse(response);

    return { data: courseData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating course from project:', error);
    return { data: null, error };
  }
};

/**
 * Generate a course from a task
 * @param {object} task - Task object with context
 * @param {object} options - Additional options
 */
export const generateCourseFromTask = async (task, options = {}) => {
  try {
    const prompt = `You are a curriculum design expert. Analyze the following task and create a course around its domain.

Task Context:
- What: ${task.activity_what || 'Untitled Task'}
- Why: ${task.activity_why || 'No purpose'}
- How: ${task.activity_how || 'No method'}

${options.depth ? `Course Depth: ${options.depth}` : ''}

Create a course that teaches the domain knowledge for this task. Return ONLY a JSON object with this exact structure:
{
  "title": "Course title",
  "description": "Course overview",
  "learning_objectives": ["Objective 1", "Objective 2"],
  "topics": ["Topic 1", "Topic 2"],
  "modules": [
    {"title": "Module 1", "description": "Description", "duration_hours": 2}
  ],
  "level": "beginner",
  "prerequisites": [],
  "generated_from": "task",
  "generation_context": {
    "task_id": "${task.id}",
    "task_what": "${task.activity_what}"
  }
}

Return ONLY the JSON object, no markdown formatting.`;

    const response = await sendLLMRequest(prompt, {
      temperature: 0.7,
      max_tokens: 1000,
    });

    const courseData = JSON.parse(response);

    return { data: courseData, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating course from task:', error);
    return { data: null, error };
  }
};

/**
 * Generate multiple tasks from a project (batch generation)
 * @param {object} project - Project object with context
 * @param {number} count - Number of tasks to generate
 */
export const generateMultipleTasksFromProject = async (project, count = 3) => {
  try {
    const tasks = [];
    const focusAreas = ['planning', 'implementation', 'testing', 'documentation', 'deployment'];

    for (let i = 0; i < Math.min(count, focusAreas.length); i++) {
      const { data, error } = await generateTaskFromProject(project, {
        focusArea: focusAreas[i],
      });

      if (data && !error) {
        tasks.push(data);
      }
    }

    return { data: tasks, error: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating multiple tasks:', error);
    return { data: null, error };
  }
};
